// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Indicator,
  Loader,
  Popover,
  Stack,
  Text,
} from '@mantine/core';
import type { Communication } from '@medplum/fhirtypes';
import { getNotificationHref, type NotificationAudience } from '@polarissleep/shared';
import { IconBell, IconBellOff } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { NotificationDropdownItem } from './NotificationDropdownItem';
import type { UseNotificationsResult } from './useNotificationsForRecipient';

const DROPDOWN_LIMIT = 10;

export interface NotificationBellProps {
  /** Result of `useNotificationsForRecipient` for the current user. */
  notificationsResult: UseNotificationsResult;
  /** Audience drives the deep-link route table. */
  audience: NotificationAudience;
  /** Where the "View all notifications" link goes (e.g. "/notifications"). */
  viewAllHref: string;
  /** Routing callback. Provided so each app can use its own router (react-router useNavigate). */
  navigate: (href: string) => void;
}

export function NotificationBell({
  notificationsResult,
  audience,
  viewAllHref,
  navigate,
}: NotificationBellProps): JSX.Element {
  const { notifications, unreadCount, loading, error, markRead, markAllRead } = notificationsResult;
  const [opened, setOpened] = useState(false);
  const visible = notifications.slice(0, DROPDOWN_LIMIT);

  const handleItemClick = useCallback(
    (notification: Communication): void => {
      const href = getNotificationHref(notification, audience);
      markRead(notification).catch(() => undefined);
      setOpened(false);
      if (href) {
        navigate(href);
      }
    },
    [audience, markRead, navigate]
  );

  const handleMarkAllRead = useCallback((): void => {
    markAllRead().catch(() => undefined);
  }, [markAllRead]);

  const handleViewAll = useCallback((): void => {
    setOpened(false);
    navigate(viewAllHref);
  }, [navigate, viewAllHref]);

  const badgeLabel = unreadCount > 9 ? '9+' : unreadCount;

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width={360} shadow="md" withinPortal>
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          radius="xl"
          aria-label="Notifications"
          onClick={() => setOpened((o) => !o)}
          styles={{ root: { overflow: 'visible' } }}
        >
          <Indicator
            disabled={unreadCount === 0}
            label={badgeLabel}
            size={18}
            color="red"
            withBorder
            styles={{
              indicator: {
                minWidth: 18,
                height: 18,
                padding: 0,
                fontSize: 11,
                fontWeight: 600,
                transform: 'translate(35%, -20%)',
              },
            }}
          >
            <IconBell />
          </Indicator>
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Group justify="space-between" align="center" px="md" py="xs">
          <Text fw={600} size="sm">
            Notifications
          </Text>
          {unreadCount > 0 && (
            <Button variant="subtle" size="compact-xs" onClick={handleMarkAllRead}>
              Mark all as read
            </Button>
          )}
        </Group>
        <Divider />
        <Box>
          {error && (
            <Text c="red" size="xs" px="md" py="sm">
              Could not load notifications.
            </Text>
          )}
          {loading && visible.length === 0 && (
            <Center py="lg">
              <Loader size="sm" />
            </Center>
          )}
          {!loading && visible.length === 0 && !error && (
            <Stack align="center" gap={4} py="lg">
              <IconBellOff size={28} stroke={1.5} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" size="sm">
                You have no notifications.
              </Text>
            </Stack>
          )}
          {visible.map((notification, index) => (
            <Box key={notification.id}>
              {index > 0 && <Divider />}
              <NotificationDropdownItem notification={notification} onClick={handleItemClick} />
            </Box>
          ))}
        </Box>
        <Divider />
        <Center py="xs">
          <Anchor component="button" type="button" size="sm" onClick={handleViewAll}>
            View all notifications
          </Anchor>
        </Center>
      </Popover.Dropdown>
    </Popover>
  );
}
